import { useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { Grid, Typography, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import LoadingButton from "@mui/lab/LoadingButton";

import BasicDialog from "../../base/BasicDialog";
import LoaderWrapper from "../../wrapper/LoaderWrapper";

import { useForm } from "../../../hooks/useForm";

import TrueFalseSelectField from "../../field/TrueFalseSelectField/trueFalseSelectField";
import DateField from "../../field/DateField/DateField";
import AutocompleteField from "../../field/AutocompleteField";
import TextFieldFormControl from "../../field/TextFieldFormControl";
import DateService from "../../../services/dateService";

import useEquipmentService from "../../../services/equipmentService";

import { useAsync, useAsyncFn } from "../../../hooks/useAsync";
import { useSnackbarAlert } from "../../../context/snackbarAlert";

import { getErrorMsg, isEmptyValue } from "../../../helpers/methods";

import FormulaService from "../../../services/formulaService";

import {
  DIALOG_PREVIEW_MODE,
  DIALOG_EDIT_MODE,
  DIALOG_CREATE_MODE,
} from "../../../helpers/constants";

const REQUIRED_FIELDS = [
  "equipment",
  "equipscheme_scripts",
  "equipscheme_start",
  "equipscheme_active",
];

const FIELDS_TO_SAVE = [...REQUIRED_FIELDS, "equipscheme_end", "tenant"];

const EquipmentSchemeDialog = (props) => {
  const { t } = useTranslation();
  const snackbarAlert = useSnackbarAlert();

  const dialogTitle = useMemo(() => {
    return props.dialogMode === DIALOG_CREATE_MODE
      ? t("dialog.equipment.add_new_equipment_scheme")
      : props.dialogMode === DIALOG_EDIT_MODE
        ? t("dialog.equipment.edit_equipment_scheme")
        : t("dialog.equipment.equipment_scheme_details");
  }, [props.dialogMode]);

  const isReadOnly = useMemo(
    () => props.readOnly || props.dialogMode === DIALOG_PREVIEW_MODE,
    [props.readOnly, props.dialogMode],
  );

  const fieldsToSave = useMemo(
    () => props.fieldsToSave || FIELDS_TO_SAVE,
    [props.fieldsToSave],
  );

  const {
    formValue,
    setFormValue,
    onChange,
    onChangeAutocompleteFieldWithObjectOptions,
    onChangeDate,
  } = useForm();

  const {
    getEquipmentSchemeDetailsData,
    createEquipmentScheme,
    updateEquipmentScheme,
    getEquipmentSchemeFilteringData,
  } = useEquipmentService();

  const initialData = useAsync(() => {
    if (props.dialogMode === DIALOG_CREATE_MODE) {
      return Promise.resolve({
        equipscheme_active: true,
        equipment: props.equipmentId,
      });
    }

    return getEquipmentSchemeDetailsData(props.equipmentSchemeId);
  }, [props.equipmentSchemeId, props.dialogMode]);

  useEffect(() => {
    setFormValue(initialData.value);
  }, [initialData.loading]);

  const prepareDataToSend = () => {
    const dataToSend = {};
    for (const [key, value] of Object.entries(formValue)) {
      if (fieldsToSave.includes(key)) {
        dataToSend[key] = value;
      }
    }

    return dataToSend;
  };
  const filteringData = useAsync(getEquipmentSchemeFilteringData);
  const updateEquipmentSchemeFn = useAsyncFn(updateEquipmentScheme);
  const addEquipmentSchemeFn = useAsyncFn(createEquipmentScheme);
  const submitFn = useMemo(
    () =>
      props.dialogMode === DIALOG_CREATE_MODE
        ? addEquipmentSchemeFn
        : updateEquipmentSchemeFn,
    [props.dialogMode],
  );

  const handleChangeMonthDate = (e) => {
    setFormValue((prev) => ({
      ...prev,
      [e.target.name]: DateService.convertDateToFormatYYYYMMDD(e.target.value),
    }));
  };

  const handleSubmit = (dataToSend) => {
    if (!FormulaService.validateMathFormula(dataToSend)) {
      snackbarAlert.openErrorSnackbarAlert(t("snackbar_alert.wrong_formula"));
      return;
    }

    submitFn
      .execute(dataToSend, props.equipmentSchemeId)
      .then((res) => {
        if (props.dialogMode === DIALOG_CREATE_MODE) {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.equipment_scheme_created"),
          );
        } else {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.equipment_scheme_updated"),
          );
        }
        if (props.onSubmitCallback) {
          props.onSubmitCallback(true);
        }
        props.onClose();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(
          getErrorMsg(
            error.data,
            t("snackbar_alert.occurred_error_during_saving_changes"),
          ),
        );
      });
  };

  const isFormValid =
    formValue &&
    REQUIRED_FIELDS.every((field) => !isEmptyValue(formValue[field]));

  const isLoading = formValue === undefined || filteringData.loading;

  const getDialogContent = () => {
    if (isLoading) return <LoaderWrapper showLoader={true} />;

    return (
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        rowGap={1}
        columnSpacing={2}
      >
        <Grid item xs={12}>
          <AutocompleteField
            name="equipment"
            isObjectOption={true}
            value={formValue.equipment}
            options={filteringData.value.equipment}
            optionLabelKey={"equipment_nr"}
            label={t("dialog.equipment.equipment")}
            onChange={onChangeAutocompleteFieldWithObjectOptions}
            multiple={false}
            readOnly
            disableClearable
          />
        </Grid>
        <Grid item xs={11}>
          <AutocompleteField
            name="tenant"
            isObjectOption={true}
            value={formValue.tenant}
            options={filteringData.value.tenant}
            optionLabelKey={"tenant_full_name"}
            label={t("dialog.equipment.tenant")}
            onChange={onChangeAutocompleteFieldWithObjectOptions}
            multiple={false}
          />
        </Grid>
        <Grid item xs={1}>
          <Tooltip title={t("dialog.equipment.scheme_tenant_info")}>
            <InfoIcon color="primary" style={{ cursor: "pointer" }} />
          </Tooltip>
        </Grid>
        <Grid item xs={6}>
          <DateField
            name={"equipscheme_start"}
            label={t("dialog.equipment.equipscheme_start")}
            value={formValue.equipscheme_start}
            onChange={handleChangeMonthDate}
            view={"month"}
            showClearIcon={false}
            maxDate={
              formValue.equipscheme_end ? formValue.equipscheme_end : undefined
            }
          />
        </Grid>
        <Grid item xs={6}>
          <DateField
            name={"equipscheme_end"}
            label={t("dialog.equipment.equipscheme_end")}
            value={formValue.equipscheme_end}
            onChange={handleChangeMonthDate}
            view={"month"}
            showClearIcon={true}
            minDate={
              formValue.equipscheme_start
                ? formValue.equipscheme_start
                : undefined
            }
          />
        </Grid>
        {props.dialogMode !== DIALOG_CREATE_MODE && (
          <Grid item xs={12}>
            <TrueFalseSelectField
              name={"equipscheme_active"}
              label={t("dialog.equipment.equipscheme_active")}
              value={formValue.equipscheme_active}
              onChange={onChange}
              addEmptyOptions={false}
              valuesAsBool
              required
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextFieldFormControl
            name="equipscheme_scripts"
            onChange={onChange}
            value={formValue.equipscheme_scripts}
            label={t("dialog.equipment.equipscheme_scripts")}
            multiline
            rows={3}
            resize={"vertical"}
          />
        </Grid>
        <Grid item xs={12} justifyContent={"flex-start"}>
          <Typography
            variant="h7"
            gutterBottom
            component="div"
            style={{
              margin: 0,
              textAlign: "left",
              color: "var(--primary)",
            }}
          >
            {t("dialog.equipment.equipscheme_scripts_note")}
          </Typography>
        </Grid>
        {!isReadOnly && (
          <Grid item xs={12}>
            <LoadingButton
              variant="contained"
              color="primary"
              fullWidth
              loading={
                addEquipmentSchemeFn.loading || updateEquipmentSchemeFn.loading
              }
              disabled={!isFormValid}
              onClick={() => handleSubmit(prepareDataToSend())}
            >
              {t("save")}
            </LoadingButton>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      contentAlign="center"
      showTopFullScreenButton={false}
      title={dialogTitle}
      maxWidth="sm"
      showDialogActions
    >
      {getDialogContent()}
    </BasicDialog>
  );
};

EquipmentSchemeDialog.propTypes = {
  dialogMode: PropTypes.oneOf([
    DIALOG_CREATE_MODE,
    DIALOG_EDIT_MODE,
    DIALOG_PREVIEW_MODE,
  ]),
  readOnly: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmitCallback: PropTypes.func,
  fieldsToSave: PropTypes.array,
  equipmentId: PropTypes.string,
  equipmentSchemeId: PropTypes.string,
};

EquipmentSchemeDialog.defaultProps = {
  open: false,
};

export default EquipmentSchemeDialog;
